package com.islamicmedia.shared.data.auth

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.auth.providers.Google

/**
 * Platform-specific Google Sign In.
 * - Android: Credential Manager account sheet → ID token → Supabase IDToken exchange (no browser)
 * - iOS: GoogleSignIn SDK's in-app sheet → ID token → Supabase IDToken exchange (no app switch)
 * - JS: browser-based OAuth redirect (the only option on the web)
 *
 * Both mobile paths fall back to the browser redirect when their client ID is unset in
 * [com.islamicmedia.shared.AppConfig], so an unconfigured build still signs in.
 *
 * @param redirectUrl only meaningful for the browser flow — see SupabaseAuthService.signInWithGoogle.
 */
expect suspend fun platformSignInWithGoogle(client: SupabaseClient, redirectUrl: String?)

/**
 * The user dismissed the native account sheet. Not a failure: callers should return to the
 * sign-in screen untouched rather than surfacing an error.
 */
class GoogleSignInCancelledException : Exception("Google sign-in cancelled")

/**
 * The original browser-redirect flow, shared by the web target and by the mobile fallbacks.
 *
 * CRITICAL: a null [redirectUrl] must mean "omit the argument", NOT "pass null". supabase-kt's
 * `redirectUrl` parameter *defaults* to the platform redirect — on mobile that's the `islamicmedia://`
 * deep link that brings the user back INTO the app. Passing null explicitly overrides that default
 * with null, so Supabase falls back to the Site URL (fitrahfilter.com) and the user is stranded in
 * the browser instead of returning to the app. Hence the branch.
 */
internal suspend fun browserSignInWithGoogle(client: SupabaseClient, redirectUrl: String?) {
    if (redirectUrl != null) {
        client.auth.signInWith(Google, redirectUrl = redirectUrl)
    } else {
        client.auth.signInWith(Google)   // let the platform default (mobile deep link) apply
    }
}
